// src/pages/SchedulePage.jsx
import React, { useState, useEffect } from 'react';
import { COLORS } from '../constants';
import { Card, Divider, LoadingSpinner } from '../components/Common';
import EmptyState from '../components/EmptyState';
import ScheduleForm from '../components/ScheduleForm';
import scheduleService from '../services/scheduleService';
import customerService from '../services/customerService';
import holidayService from '../services/holidayService';
import { buildCalendarMatrix, toTimeStr, utcIsoToKstParts } from '../utils';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

function normalizeDatePart(value) {
  if (!value) return '';

  const raw = String(value).trim();
  if (!raw) return '';

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const digits = raw.replace(/\D/g, '');

  if (digits.length >= 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }

  return '';
}

function buildAnnualDate(value, year) {
  if (!value) return '';

  const raw = String(value).trim();
  if (!raw) return '';

  const iso = normalizeDatePart(raw);
  if (iso) {
    const [, month, day] = iso.split('-');
    if (month && day) return `${year}-${month}-${day}`;
  }

  const digits = raw.replace(/\D/g, '');

  if (digits.length === 6) {
    return `${year}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
  }

  if (digits.length === 4) {
    return `${year}-${digits.slice(0, 2)}-${digits.slice(2, 4)}`;
  }

  return '';
}

function isValidDateStr(dateStr) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr || '')) return false;

  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function makeAutoSchedule({
  id,
  title,
  customer,
  dateStr,
  icon,
  color,
  memo,
}) {
  return {
    id,
    title,
    customer_name: customer?.name || '',
    customers: { name: customer?.name || '' },
    scheduled_at: `${dateStr}T09:00:00`,
    schedule_icon: icon,
    color,
    memo,
    next_action: '',
    is_auto_schedule: true,
  };
}

function buildCustomerAutoSchedules(customers = [], start, end) {
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T23:59:59`);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return [];
  }

  const years = [];
  for (let year = startDate.getFullYear(); year <= endDate.getFullYear(); year += 1) {
    years.push(year);
  }

  const events = [];

  (customers || []).forEach((customer) => {
    const customerId = customer.id || customer.app_customer_id || customer.db_id || customer.name;

    years.forEach((year) => {
      const birthday = buildAnnualDate(customer.birth, year);

      if (isValidDateStr(birthday)) {
        const birthdayDate = new Date(`${birthday}T09:00:00`);

        if (birthdayDate >= startDate && birthdayDate <= endDate) {
          events.push(
            makeAutoSchedule({
              id: `birthday-${customerId}-${birthday}`,
              title: `🎂 ${customer.name}님 생일`,
              customer,
              dateStr: birthday,
              icon: '🎂',
              color: '#FCE7F3',
              memo: '고객 생일입니다.',
            })
          );
        }
      }
    });

    const dueDate = normalizeDatePart(customer.due_date);

    if (isValidDateStr(dueDate)) {
      const targetDate = new Date(`${dueDate}T09:00:00`);

      if (targetDate >= startDate && targetDate <= endDate) {
        events.push(
          makeAutoSchedule({
            id: `due-${customerId}-${dueDate}`,
            title: `👶 ${customer.name}님 출산예정일`,
            customer,
            dateStr: dueDate,
            icon: '👶',
            color: '#DBEAFE',
            memo: customer.baby_name ? `태명: ${customer.baby_name}` : '출산예정일입니다.',
          })
        );
      }
    }

    const carExpiry = normalizeDatePart(customer.car_expiry);

    if (isValidDateStr(carExpiry)) {
      const targetDate = new Date(`${carExpiry}T09:00:00`);

      if (targetDate >= startDate && targetDate <= endDate) {
        events.push(
          makeAutoSchedule({
            id: `car-${customerId}-${carExpiry}`,
            title: `🚗 ${customer.name}님 자동차보험 만기`,
            customer,
            dateStr: carExpiry,
            icon: '🚗',
            color: '#FEF3C7',
            memo: customer.car_number ? `차량번호: ${customer.car_number}` : '자동차보험 만기일입니다.',
          })
        );
      }
    }
  });

  return events;
}

function sortSchedulesByTime(items = []) {
  return [...items].sort((a, b) => {
    const aTime = toTimeStr(a.scheduled_at);
    const bTime = toTimeStr(b.scheduled_at);
    return aTime.localeCompare(bTime);
  });
}


function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 달력 컴포넌트 ─────────────────────────────
function Calendar({
  year,
  month,
  selDay,
  today,
  monthSchedules = [],
  holidays = [],
  onPrev,
  onNext,
  onSelect,
  compact = false
}) {
  const firstDay = new Date(year, month, 1);
  const startDay = firstDay.getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  const prevLastDate = new Date(year, month, 0).getDate();

  const days = [];

  for (let i = startDay - 1; i >= 0; i--) {
    days.push({
      day: prevLastDate - i,
      current: false,
    });
  }

  for (let d = 1; d <= lastDate; d++) {
    days.push({
      day: d,
      current: true,
    });
  }

  while (days.length % 7 !== 0) {
    days.push({
      day: days.length - startDay - lastDate + 1,
      current: false,
    });
  }

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  const holidayMap = new Map();
  holidays.forEach((holiday) => {
    const names = holidayMap.get(holiday.date) || [];
    if (!names.includes(holiday.name)) names.push(holiday.name);
    holidayMap.set(holiday.date, names);
  });

  return (
    <Card
      style={{
        padding: compact ? '12x 4px' : '18px 14px',
        marginLeft: compact ? -8 : 0,
        marginRight: compact ? -8 : 0,
      }}
    >
      {/* 헤더 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
          padding: '0 8px',
        }}
      >
        <button
          onClick={onPrev}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 22,
            cursor: 'pointer',
            color: COLORS.textGray,
          }}
        >
          ‹
        </button>

        <span
          style={{
            fontWeight: 800,
            fontSize: 20,
            color: COLORS.text,
          }}
        >
          {year}년 {month + 1}월
        </span>

        <button
          onClick={onNext}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 22,
            cursor: 'pointer',
            color: COLORS.textGray,
          }}
        >
          ›
        </button>
      </div>

      {/* 요일 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          marginBottom: 8,
        }}
      >
        {DAY_LABELS.map((d, i) => (
          <div
            key={d}
            style={{
              textAlign: 'center',
              fontSize: 13,
              fontWeight: 700,
              padding: '6px 0',
              color:
                i === 0
                  ? '#FCA5A5'
                  : i === 6
                  ? '#93C5FD'
                  : '#9CA3AF',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* 날짜 */}
      {weeks.map((week, wi) => (
        <div
          key={wi}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: 0,
          }}
        >
          {week.map((cell, di) => {
            const day = cell.day;
            const isCurrent = cell.current;

            const isSel = isCurrent && day === selDay;

            const isToday =
              isCurrent &&
              day === today.getDate() &&
              month === today.getMonth() &&
              year === today.getFullYear();

            const isSun = di === 0;
            const isSat = di === 6;

            const dateKey = isCurrent
              ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
              : null;

            const daySchedules = dateKey
              ? monthSchedules.filter(
                  (s) =>
                    utcIsoToKstParts(s.scheduled_at).date === dateKey
                )
              : [];
            const holidayNames = dateKey ? holidayMap.get(dateKey) || [] : [];
            const isHoliday = holidayNames.length > 0;
            const holidayLabel = holidayNames.join(', ');

            return (
              <button
                key={`${wi}-${di}`}
                disabled={!isCurrent}
                onClick={() => isCurrent && onSelect(day)}
                style={{
                  minHeight: compact ? 68 : 96,
                  padding: '5px 3px',
                  borderRadius: 10,
                  border: isSel
                    ? `2px solid ${COLORS.primary}`
                    : '1px solid transparent',
                  background: isSel
                    ? COLORS.primaryBg
                    : '#fff',
                  cursor: isCurrent ? 'pointer' : 'default',

                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',

                  gap: 3,
                  overflow: 'hidden',
                }}
              >
                {/* 날짜 숫자 */}
                <div
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    paddingLeft: 2,
                    fontSize: 13,
                    fontWeight:
                      isSel || isToday ? 800 : 500,

                    color: !isCurrent
                      ? isSun
                        ? '#FECACA'
                        : isSat
                        ? '#BFDBFE'
                        : '#D1D5DB'
                      : isHoliday || isSun
                      ? '#F87171'
                      : isToday
                      ? COLORS.primary
                      : isSat
                      ? '#60A5FA'
                      : COLORS.text,
                  }}
                >
                  {day}
                </div>

                {isHoliday && (
                  <div
                    title={holidayLabel}
                    aria-label={`공휴일: ${holidayLabel}`}
                    style={{
                      width: '100%',
                      paddingLeft: 2,
                      paddingRight: 2,
                      color: '#DC2626',
                      fontSize: compact ? 9 : 10,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'left',
                    }}
                  >
                    {holidayLabel}
                  </div>
                )}

                {/* 일정 */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 3,
                    width: '100%',
                  }}
                >
                  {daySchedules.slice(0, 2).map((s, idx) => {
                    const cleanTitle = (
                      s.title || ''
                    ).replace(/^[^\s]+\s/, '');

                    const icon =
                      s.schedule_icon || '📌';

                    return (
                      <div
                        key={s.id || idx}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          width: 'fit-content',
                          maxWidth: '100%',

                          background:
                            s.color || '#E5D4FF',

                          color: '#333',
                          borderRadius: 999,
                          padding: '2px 5px',
                          fontSize: 10,
                          lineHeight: 1.2,

                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <span style={{ marginRight: 2 }}>
                          {icon}
                        </span>

                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {cleanTitle}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {daySchedules.length > 2 && (
                  <div
                    style={{
                      fontSize: 10,
                      color: COLORS.textGray,
                      paddingLeft: 2,
                    }}
                  >
                    +{daySchedules.length - 2}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </Card>
  );
}

// ── 일정 목록 컴포넌트 ────────────────────────
function ScheduleList({ schedules, loading, dayLabel, onAdd, onEdit, onDelete }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 16, color: COLORS.text }}>
          {dayLabel} 일정
        </span>

        <button
          onClick={onAdd}
          style={{
            background: COLORS.primary,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '8px 16px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + 일정 추가
        </button>
      </div>

      <Card style={{ padding: 0 }}>
        {loading ? (
          <LoadingSpinner />
        ) : schedules.length === 0 ? (
          <EmptyState
            icon="📅"
            message="일정이 없습니다"
            sub="+ 일정 추가 버튼을 눌러보세요"
          />
        ) : (
          schedules.map((s, i) => {
            const customerName = s.customers?.name || s.customer_name || '';
            const cleanTitle = (s.title || '').replace(/^[^\s]+\s/, '');
            const icon = s.schedule_icon || '📌';

            return (
              <React.Fragment key={s.id || i}>
                <div
                  onClick={() => !s.is_auto_schedule && onEdit(s)}
                  style={{
                    padding: '14px 20px',
                    cursor: s.is_auto_schedule ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  {/* 시간 */}
                  <div
                    style={{
                      background: s.color || COLORS.primaryBg,
                      borderRadius: 12,
                      padding: '8px 12px',
                      flexShrink: 0,
                      textAlign: 'center',
                      minWidth: 60,
                    }}
                  >
                    <div style={{
                      fontWeight: 800,
                      color: COLORS.primary,
                      fontSize: 14,
                    }}>
                      {toTimeStr(s.scheduled_at)}
                    </div>
                  </div>

                  {/* 내용 */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: COLORS.text,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {icon} {cleanTitle}
                    </div>

                    {customerName && (
                      <div style={{
                        fontSize: 12,
                        color: COLORS.textGray,
                        marginTop: 3,
                      }}>
                        {customerName} 고객
                      </div>
                    )}

                    {s.memo && (
                      <div style={{
                        fontSize: 12,
                        color: COLORS.textGray,
                        marginTop: 5,
                        lineHeight: 1.4,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {s.memo}
                      </div>
                    )}

                    {s.next_action && (
                      <div style={{
                        marginTop: 6,
                        fontSize: 11,
                        color: COLORS.primary,
                        fontWeight: 700,
                      }}>
                        다음 액션: {s.next_action}
                      </div>
                    )}
                  </div>

                  {/* 수정/삭제 */}
                  {!s.is_auto_schedule && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      alignItems: 'center',
                      flexShrink: 0,
                    }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(s);
                        }}
                        style={{
  border: 'none',
  background: '#EEF2FF',
  color: COLORS.primary,
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  minWidth: 52,
}}
                      >
                        수정
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(s);
                        }}
                        style={{
  border: 'none',
  background: '#FEE2E2',
  color: '#DC2626',
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  minWidth: 52,
}}
                      >
                        삭제
                      </button>
                    </div>
                  )}
                </div>

                {i < schedules.length - 1 && (
                  <Divider style={{ margin: '0 20px' }} />
                )}
              </React.Fragment>
            );
          })
        )}
      </Card>
    </div>
  );
}

// ── 메인 ─────────────────────────────────────
export default function SchedulePage({ initialSchedule }) {
  const isMobile = useIsMobile();
  const today    = new Date();
  const [year, setYear]     = useState(today.getFullYear());
  const [month, setMonth]   = useState(today.getMonth());
  const [selDay, setSelDay] = useState(today.getDate());
  const [schedules, setSchedules] = useState([]);
  const [monthSchedules, setMonthSchedules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [editItem, setEditItem]   = useState(null);
useEffect(() => {
  if (!initialSchedule) return;

  const targetDate =
    initialSchedule.dateStr || new Date().toISOString().slice(0, 10);

  const [y, m, d] = targetDate.split('-').map(Number);

  if (y && m && d) {
    setYear(y);
    setMonth(m - 1);
    setSelDay(d);
  }

  setEditItem({
    title: initialSchedule.title || '상담 후속 일정',
    customer_name: initialSchedule.customer_name || '',
    memo: initialSchedule.memo || '',
    next_action: initialSchedule.next_action || '',
    schedule_type: initialSchedule.schedule_type || 'phone',
    schedule_icon: initialSchedule.schedule_icon || '📞',
    color: initialSchedule.color || '#E5D4FF',
    scheduled_at: `${targetDate}T09:00:00`,
    reminder_minutes: initialSchedule.reminder_minutes ?? '60',
  });

  setShowForm(true);
}, [initialSchedule]);

  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(selDay).padStart(2, '0')}`;
  const matrix  = buildCalendarMatrix(year, month);
  const DAY_KR  = ['일', '월', '화', '수', '목', '금', '토'];
  const dayLabel = `${month + 1}월 ${selDay}일 (${DAY_KR[new Date(year, month, selDay).getDay()]})`;

useEffect(() => {
  loadDay();
  loadMonth();
}, [dateStr, month, year]);

useEffect(() => {
  let active = true;
  setHolidays([]);
  holidayService.fetchByYear(year).then((items) => {
    if (active) setHolidays(items);
  });
  return () => { active = false; };
}, [year]);

async function loadMonth() {
  try {
    const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDate = new Date(year, month + 1, 0).getDate();
    const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate).padStart(2, '0')}`;

    const [{ data, error }, customers] = await Promise.all([
      scheduleService.getMonthSchedules(start, end),
      customerService.list(),
    ]);

    if (error) throw error;

    const autoSchedules = buildCustomerAutoSchedules(customers, start, end);
    setMonthSchedules(sortSchedulesByTime([...(data || []), ...autoSchedules]));
  } catch (e) {
    console.error(e);
  }
}

async function loadDay() {
    setLoading(true);
    try {
      const [daySchedules, customers] = await Promise.all([
        scheduleService.listByDate(dateStr),
        customerService.list(),
      ]);

      const autoSchedules = buildCustomerAutoSchedules(customers, dateStr, dateStr);
      setSchedules(sortSchedulesByTime([...(daySchedules || []), ...autoSchedules]));
    }
    catch(e) { setSchedules([]); }
    finally { setLoading(false); }
  }

  function prevMonth() {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
    setSelDay(1);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
    setSelDay(1);
  }

  // ── 모바일 레이아웃 ───────────────────────────
  if (isMobile) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',        // ✅ minHeight → height
      overflow: 'hidden',    // ✅ visible → hidden
    }}>
      <div style={{
        background: COLORS.white,
        padding: '14px 20px',
        borderBottom: `1px solid ${COLORS.border}`,
        flexShrink: 0,
        textAlign: 'center',
      }}>
        <span style={{ fontWeight: 700, fontSize: 17, color: COLORS.text }}>
          일정 관리
        </span>
      </div>

      {/* 전체 스크롤 영역 */}
      <div style={{
        flex: 1,
        overflowY: 'auto',           // ✅ 여기서 스크롤
        WebkitOverflowScrolling: 'touch',
        padding: '14px 16px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}>
        <Calendar
          year={year}
          month={month}
          selDay={selDay}
          today={today}
          onPrev={prevMonth}
          onNext={nextMonth}
          onSelect={setSelDay}
          monthSchedules={monthSchedules}
          holidays={holidays}
          compact={true}
        />

        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.text }}>
              {dayLabel} 일정
            </span>
            <button
              onClick={() => { setEditItem(null); setShowForm(true); }}
              style={{
                border: 'none', background: COLORS.primary,
                color: '#fff', borderRadius: 999,
                padding: '8px 12px', fontSize: 12,
                fontWeight: 800, cursor: 'pointer',
              }}
            >
              + 추가
            </button>
          </div>

          <Card style={{ padding: 0 }}>
            {loading ? (
              <LoadingSpinner />
            ) : schedules.length === 0 ? (
              <EmptyState
                icon="📅"
                message="일정이 없습니다"
                sub="+ 추가 버튼으로 일정을 등록하세요"
              />
            ) : (
              schedules.map((s, i) => {
                const customerName = s.customers?.name || s.customer_name || '';
                const cleanTitle = (s.title || '').replace(/^[^\s]+\s/, '');
                const icon = s.schedule_icon || '📌';
                return (
                  <React.Fragment key={s.id || i}>
                    <div
                      onClick={() => { if (!s.is_auto_schedule) { setEditItem(s); setShowForm(true); } }}
                      style={{ padding: '13px 16px', cursor: s.is_auto_schedule ? 'default' : 'pointer' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{
                          background: s.color || COLORS.primaryBg,
                          color: COLORS.primary, borderRadius: 12,
                          padding: '8px 10px', fontWeight: 800,
                          fontSize: 13, minWidth: 52,
                          textAlign: 'center', flexShrink: 0,
                        }}>
                          {toTimeStr(s.scheduled_at)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 700, fontSize: 14, color: COLORS.text,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {icon} {cleanTitle}
                          </div>
                          {customerName && (
                            <div style={{ fontSize: 12, color: COLORS.textGray, marginTop: 2 }}>
                              {customerName} 고객
                            </div>
                          )}
                          {s.memo && (
                            <div style={{
                              fontSize: 12, color: COLORS.textGray,
                              marginTop: 4, lineHeight: 1.4, whiteSpace: 'pre-wrap',
                            }}>
                              {s.memo}
                            </div>
                          )}
                          {s.next_action && (
                            <div style={{ marginTop: 6, fontSize: 11, color: COLORS.primary, fontWeight: 700 }}>
                              다음 액션: {s.next_action}
                            </div>
                          )}
                        </div>
                        {!s.is_auto_schedule && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                            <button
                              type="button"
                              onClick={e => { e.stopPropagation(); setEditItem(s); setShowForm(true); }}
                              style={{
                                border: 'none', background: '#EEF2FF',
                                color: COLORS.primary, borderRadius: 999,
                                padding: '6px 10px', cursor: 'pointer',
                                fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
                              }}
                            >수정</button>
                            <button
                              type="button"
                              onClick={async e => {
                                e.stopPropagation();
                                if (window.confirm('일정을 삭제할까요?')) {
                                  await scheduleService.remove(s.id);
                                  loadDay(); loadMonth();
                                }
                              }}
                              style={{
                                border: 'none', background: '#FEE2E2',
                                color: '#DC2626', borderRadius: 999,
                                padding: '6px 10px', cursor: 'pointer',
                                fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
                              }}
                            >삭제</button>
                          </div>
                        )}
                      </div>
                    </div>
                    {i < schedules.length - 1 && <Divider style={{ margin: '0 16px' }} />}
                  </React.Fragment>
                );
              })
            )}
          </Card>
        </div>
      </div>

      <ScheduleForm
        visible={showForm}
        onClose={() => { setShowForm(false); setEditItem(null); }}
        onSave={() => { loadDay(); loadMonth(); setShowForm(false); setEditItem(null); }}
        dateStr={dateStr}
        initial={editItem}
      />
    </div>
  );
}

  // ── PC 레이아웃 ───────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 0 40px' }}>

        {/* PC: 좌우 2컬럼 */}
<div
  style={{
    display: 'grid',
    gridTemplateColumns: '580px 1fr',
    gap: 24,
    alignItems: 'start',
  }}
>
  {/* 왼쪽: 달력 */}
  <Calendar
    year={year}
    month={month}
    selDay={selDay}
    today={today}
    monthSchedules={monthSchedules}
    holidays={holidays}
    onPrev={prevMonth}
    onNext={nextMonth}
    onSelect={setSelDay}
    compact={false}
  />

  {/* 오른쪽: 일정 목록 */}
  <ScheduleList
    schedules={schedules}
    loading={loading}
    dayLabel={dayLabel}
    onAdd={() => {
      setEditItem(null);
      setShowForm(true);
    }}
    onEdit={(s) => {
      setEditItem(s);
      setShowForm(true);
    }}
    onDelete={async (s) => {
      if (window.confirm('일정을 삭제할까요?')) {
        await scheduleService.remove(s.id);
        loadDay();
        loadMonth();
      }
    }}
  />
</div>
      </div>

      <ScheduleForm visible={showForm} onClose={() => { setShowForm(false); setEditItem(null); }} onSave={() => { loadDay(); loadMonth(); setShowForm(false); setEditItem(null); }} dateStr={dateStr} initial={editItem} />
    </div>
  );
}
